import type {
    ChatApiVersion,
    ChatDoc,
    ChatErrorContext,
    ChatService,
    ChatType
} from '@odo/shared-ui';
import { createChatServiceV2 } from '@odo/shared-ui';
import apiV2 from '../api/v2';
import { uploadFile as uploadCloudFile } from './cloud-files';
import { reportError } from './errorReporting';
import { captureUploadError } from '../observability/sentry';
import {
    editPatientChatMessage,
    fetchChatStatus,
    fetchPatientChat,
    sendPatientChatMessage
} from './patient';

/**
 * dashboard's binding of the shared chat provider to its own API layer.
 * Deletion and the activity log are ODO-only, so those capabilities are absent.
 */
export const chatService = {
    fetchChat: (patientId: string) => fetchPatientChat(patientId),
    fetchChatStatus: (patientId: string) => fetchChatStatus(patientId),
    sendMessage: (patientId: string, message: string, _chatType?: ChatType, doc?: ChatDoc) =>
        sendPatientChatMessage(patientId, message, doc),
    editMessage: (patientId: string, messageId: string, message: string, doc?: ChatDoc) =>
        editPatientChatMessage(patientId, messageId, message, doc),
    uploadFile: async (
        patientId: string,
        file: File,
        updateId: string | number | undefined,
        onProgress?: (progress: number) => void
    ): Promise<{ fileId: string }> => {
        try {
            const patientFile = await uploadCloudFile(
                patientId,
                file,
                'CHAT_ATTACHMENT',
                undefined,
                updateId === undefined ? undefined : { updateId: String(updateId) },
                onProgress
            );
            return { fileId: patientFile.fileId };
        } catch (err) {
            reportError({
                source: 'dashboard',
                message: `Chat file upload failed: ${err instanceof Error ? err.message : 'Upload failed'}`,
                stack: err instanceof Error ? err.stack : undefined,
                url: window.location.href,
                pathname: window.location.pathname,
                userAgent: navigator.userAgent,
                errorType: 'upload_failure',
                metadata: {
                    patientId,
                    fileName: file.name,
                    fileSize: file.size,
                    context: 'chat'
                }
            });
            captureUploadError(err, {
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type,
                uploadContext: 'chat'
            });
            throw err;
        }
    },
    onError: (err: unknown, context: ChatErrorContext): void => {
        reportError({
            source: `PatientChatProvider.${context.scope}`,
            message: err instanceof Error ? err.message : String(err),
            stack: err instanceof Error ? err.stack : undefined,
            url: window.location.href,
            pathname: window.location.pathname,
            userAgent: navigator.userAgent,
            errorType: 'unhandledrejection',
            metadata: { patientId: context.patientId }
        });
    }
} satisfies ChatService;

/**
 * The same port, served by /api/v2: the conversation comes from the database
 * instead of Monday. Deletion and the activity log stay absent here as they are
 * on v1 — they are ODO-only capabilities, and the provider degrades on them.
 */
export const chatServiceV2: ChatService = {
    ...createChatServiceV2({
        http: apiV2,
        uploadFile: chatService.uploadFile,
        onError: chatService.onError
    }),
    deleteMessage: undefined,
    deleteMessageFile: undefined,
    fetchActivities: undefined
};

/** Picks the implementation for a version. The only place that mapping lives. */
export function getChatService(version: ChatApiVersion): ChatService {
    return version === 'v2' ? chatServiceV2 : chatService;
}
